import { toLocalDatetimeInputValue } from '@/utils/commonUtils'

/**
 * Validate thứ tự thời gian chứng từ bán / kho.
 * So sánh theo timestamp đầy đủ (datetime) hoặc theo ngày lịch (YYYY-MM-DD).
 */

/** Chuỗi local `YYYY-MM-DDTHH:mm` cho DateTimeInput từ ISO / date / Date. */
export const toNgayChungTuLocalInput = (
  raw: string | Date | null | undefined,
): string => {
  if (raw == null || raw === '') return ''
  if (typeof raw === 'string') {
    const s = raw.trim()
    // Chỉ coi là wall-clock local khi không có Z/offset (datetime-local / form).
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 16)
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s}T00:00`
  }
  const d = raw instanceof Date ? raw : new Date(String(raw).trim())
  if (Number.isNaN(d.getTime())) return ''
  return toLocalDatetimeInputValue(d)
}

/** Payload API: ISO UTC từ `YYYY-MM-DDTHH:mm` / date / ISO. */
export const toNgayChungTuApiValue = (raw: string | null | undefined): string => {
  const s = String(raw ?? '').trim()
  if (!s) return ''
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)
    ? `${s}:00`
    : /^\d{4}-\d{2}-\d{2}$/.test(s)
      ? `${s}T00:00:00`
      : s
  const d = new Date(normalized)
  return Number.isNaN(d.getTime()) ? s : d.toISOString()
}

const toTime = (raw: string | Date | null | undefined): number | null => {
  if (raw == null || raw === '') return null
  if (raw instanceof Date) {
    const t = raw.getTime()
    return Number.isNaN(t) ? null : t
  }
  const s = String(raw).trim()
  if (!s) return null
  // datetime-local `YYYY-MM-DDTHH:mm` — thêm giây để parse ổn định trên mọi engine.
  const normalized = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s) ? `${s}:00` : s
  const d = new Date(normalized)
  const t = d.getTime()
  return Number.isNaN(t) ? null : t
}

/** Ngày lịch local YYYY-MM-DD từ ISO/datetime/date. */
export const toLocalDateOnly = (raw: string | Date | null | undefined): string => {
  if (raw == null || raw === '') return ''
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  }
  const d = raw instanceof Date ? raw : new Date(String(raw).trim())
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Thời gian giao không được trước thời gian đơn/HĐ.
 * Trả true nếu hợp lệ hoặc thiếu một trong hai (không chặn khi chưa nhập).
 */
export const isDeliveryTimeValid = (
  orderTime: string | Date | null | undefined,
  deliveryTime: string | Date | null | undefined,
): boolean => {
  const orderMs = toTime(orderTime)
  const deliveryMs = toTime(deliveryTime)
  if (orderMs == null || deliveryMs == null) return true
  return deliveryMs >= orderMs
}

/** So sánh `YYYY-MM-DDTHH:mm`. Thiếu một vế → không coi là trước mốc. */
export const isLocalDatetimeBefore = (
  value: string | null | undefined,
  min: string | null | undefined,
): boolean => {
  const v = String(value ?? '').trim().slice(0, 16)
  const m = String(min ?? '').trim().slice(0, 16)
  if (v.length < 16 || m.length < 16) return false
  return v < m
}

/**
 * Mốc sớm nhất của hạn giao hàng: không trước thời điểm đơn
 * và không trước phút hiện tại.
 */
export const earliestDeliveryLocal = (orderTime: string | null | undefined): string => {
  const now = toLocalDatetimeInputValue(new Date())
  const order = String(orderTime ?? '').trim().slice(0, 16)
  if (order.length < 16) return now
  return order > now ? order : now
}

export type DeliveryDeadlineIssue = 'beforeOrder' | 'beforeNow'

/**
 * Hạn giao phải sau thời điểm đơn.
 * Hạn mới không được trước phút hiện tại.
 * Sửa phiếu mà giữ nguyên hạn đã lưu thì không chặn hạn đã qua.
 */
export const deliveryDeadlineIssue = (input: {
  deliveryTime: string | null | undefined
  orderTime: string | null | undefined
  savedDeliveryTime?: string | null
  isEdit?: boolean
}): DeliveryDeadlineIssue | null => {
  const value = String(input.deliveryTime ?? '').trim().slice(0, 16)
  if (value.length < 16) return null
  const order = String(input.orderTime ?? '').trim().slice(0, 16)
  if (isLocalDatetimeBefore(value, order)) return 'beforeOrder'
  const saved = String(input.savedDeliveryTime ?? '').trim().slice(0, 16)
  if (input.isEdit && saved.length >= 16 && value === saved) return null
  if (isLocalDatetimeBefore(value, earliestDeliveryLocal(input.orderTime))) return 'beforeNow'
  return null
}

/**
 * Ngày chứng từ XK/NK: nhập ngày nào ghi nhận ngày đó — không chặn ngược/tương lai.
 * `_ngayTao` giữ chữ ký cũ (form vẫn truyền), không dùng để validate.
 */
export const isNgayChungTuValid = (
  _ngayChungTu?: string | Date | null,
  _ngayTao?: string | Date | null,
): boolean => true
